/**
 * Create Room Page
 *
 * Form for creating a new geofenced room.
 */

import { useState, useEffect, useCallback } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useGeolocation } from '../hooks/useGeolocation'
import { generateRoomSecret } from '../lib/crypto'
import { saveRoom } from '../lib/storage'
import {
  GEOFENCE_RADIUS_OPTIONS,
  type GeofenceRadius,
  type AllowedTransportsConfig,
  DEFAULT_ALLOWED_TRANSPORTS,
} from '../lib/types'
import { ManualLocationInput } from '../components/common/ManualLocationInput'
import { ShareRoom } from '../components/common/ShareRoom'
import { LocationAccuracyToggle } from '../components/common/LocationAccuracyToggle'
import {
  getStoredAccuracyMode,
  type LocationAccuracyMode,
} from '../components/common/locationAccuracyUtils'
import { ConnectionTypeSelector } from '../components/common/ConnectionTypeSelector'
import { generateAnonName } from '../lib/names'
import { v4 as uuidv4 } from 'uuid'

interface CreatedRoom {
  id: string
  name: string
  secret: string
  pin?: string
}

export function CreatePage() {
  const navigate = useNavigate()
  const [accuracyMode, setAccuracyMode] = useState<LocationAccuracyMode>(() =>
    getStoredAccuracyMode()
  )

  const {
    location,
    error: geoError,
    requestPermission,
    isLoading,
    locationSource,
    setManualLocation,
    clearManualLocation,
  } = useGeolocation({ accuracyMode, enableIpFallback: true })

  const [roomName, setRoomName] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [radius, setRadius] = useState<GeofenceRadius>(150)
  const [pin, setPin] = useState('')
  const [usePin, setUsePin] = useState(false)
  const [allowedTransports, setAllowedTransports] = useState<AllowedTransportsConfig>(
    DEFAULT_ALLOWED_TRANSPORTS
  )
  const [isCreating, setIsCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [createdRoom, setCreatedRoom] = useState<CreatedRoom | null>(null)

  // Re-request location when accuracy mode changes
  const handleAccuracyChange = useCallback(
    (mode: LocationAccuracyMode) => {
      setAccuracyMode(mode)
      // Clear current location and re-request with new accuracy
      clearManualLocation()
      // Small delay to ensure state updates before re-requesting
      setTimeout(() => {
        requestPermission()
      }, 100)
    },
    [clearManualLocation, requestPermission]
  )

  // Generate a random display name on mount
  useEffect(() => {
    const savedName = sessionStorage.getItem('displayName')
    if (savedName) {
      setDisplayName(savedName)
    } else {
      setDisplayName(generateAnonName())
    }
  }, [])


  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!roomName.trim()) {
      setError('Room name is required')
      return
    }

    if (!displayName.trim()) {
      setError('Display name is required')
      return
    }

    if (!location) {
      setError('Location is required to create a geofenced room')
      return
    }

    // Validate PIN if enabled
    if (usePin && pin.length < 4) {
      setError('PIN must be at least 4 digits')
      return
    }

    setIsCreating(true)
    setError(null)

    try {
      const roomId = uuidv4()
      const roomSecret = generateRoomSecret()

      const room = {
        id: roomId,
        name: roomName.trim(),
        center: location,
        radiusMeters: radius,
        createdAt: new Date(),
        creatorPeerId: '', // Will be set when joining
        secret: roomSecret,
        pin: usePin ? pin : undefined,
      }

      // Save room to local storage
      await saveRoom(room, roomSecret)

      // Store PIN in session if set
      if (usePin && pin) {
        sessionStorage.setItem(`room_${roomId}_pin`, pin)
      }

      // Store display name and role for the session
      sessionStorage.setItem('displayName', displayName.trim())
      sessionStorage.setItem(`room_${roomId}_secret`, roomSecret)
      sessionStorage.setItem(`room_${roomId}_role`, 'organizer')

      // Store allowed transports configuration
      sessionStorage.setItem(
        `room_${roomId}_allowedTransports`,
        JSON.stringify(allowedTransports)
      )

      // Show share screen instead of navigating directly
      setCreatedRoom({
        id: roomId,
        name: roomName.trim(),
        secret: roomSecret,
        pin: usePin ? pin : undefined,
      })
      setIsCreating(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create room')
      setIsCreating(false)
    }
  }

  // Navigate to room
  const handleGoToRoom = () => {
    if (createdRoom) {
      navigate(`/room/${createdRoom.id}`)
    }
  }

  // Show share screen after room creation
  if (createdRoom) {
    return (
      <div className="flex flex-col overflow-hidden" style={{ height: '100%' }}>
        {/* Header */}
        <header className="flex-shrink-0 flex items-center gap-4 p-4 border-b border-white/10">
          <h1 className="text-lg font-display font-bold flex items-center gap-2">
            <span className="text-green-400">&#10003;</span>
            Room Created!
          </h1>
        </header>

        {/* Share content - scrollable */}
        <main
          className="flex-1 min-h-0 p-4 overflow-y-auto scrollbar-hide"
          style={{
            WebkitOverflowScrolling: 'touch',
            overscrollBehaviorY: 'contain',
            touchAction: 'pan-y'
          }}
        >
          <ShareRoom
            roomId={createdRoom.id}
            roomSecret={createdRoom.secret}
            roomName={createdRoom.name}
            pin={createdRoom.pin}
          />

          {/* Go to Room button */}
          <button
            onClick={handleGoToRoom}
            className="w-full mt-6 py-4 rounded-xl bg-primary text-white font-bold text-lg shadow-hard hover:scale-[1.02] active:scale-[0.98] transition-transform"
            style={{ marginBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
          >
            Go to Room
          </button>
        </main>
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Header */}
      <header className="flex-shrink-0 flex items-center gap-3 px-3 py-2 border-b border-white/10 bg-black/40">
        <Link to="/" className="text-white/60 hover:text-primary text-xs">← Back</Link>
        <h1 className="text-sm font-display font-bold text-primary">Create Room</h1>
      </header>

      {/* Form - scrollable */}
      <main className="flex-1 min-h-0 p-3 overflow-y-auto scrollbar-hide">
        <form onSubmit={handleCreate} className="flex flex-col gap-2">
          {/* Room Name & Display Name */}
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="block text-[10px] text-white/60 mb-1">Room Name</label>
              <input
                type="text"
                value={roomName}
                onChange={(e) => setRoomName(e.target.value)}
                placeholder="e.g., March"
                className="input text-sm py-1.5"
                maxLength={50}
              />
            </div>
            <div className="flex-1">
              <label className="block text-[10px] text-white/60 mb-1">Your Name</label>
              <div className="flex gap-1">
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Scout 1"
                  className="input flex-1 text-sm py-1.5"
                  maxLength={30}
                />
                <button
                  type="button"
                  onClick={() => setDisplayName(generateAnonName())}
                  className="px-2 py-1 rounded bg-white/10 text-sm"
                >
                  🎲
                </button>
              </div>
            </div>
          </div>

          {/* Location */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10px] text-white/60">Location</label>
              <LocationAccuracyToggle value={accuracyMode} onChange={handleAccuracyChange} compact />
            </div>
            <ManualLocationInput
              currentLocation={location}
              locationSource={locationSource}
              error={geoError}
              isLoading={isLoading}
              onSetManualLocation={setManualLocation}
              onClearManualLocation={clearManualLocation}
              onRetryAutoLocation={requestPermission}
            />
          </div>

          {/* Radius */}
          <div>
            <label className="block text-[10px] text-white/60 mb-1">Radius</label>
            <div className="flex flex-wrap gap-1">
              {GEOFENCE_RADIUS_OPTIONS.map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRadius(r)}
                  className={`px-2 py-1 rounded text-xs ${radius === r
                    ? 'bg-primary text-white'
                    : 'bg-white/10 text-white/60'
                  }`}
                >
                  {r}m
                </button>
              ))}
            </div>
          </div>

          {/* PIN */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="usePin"
              checked={usePin}
              onChange={(e) => setUsePin(e.target.checked)}
              className="w-4 h-4"
            />
            <label htmlFor="usePin" className="text-xs text-white/60">Require PIN</label>
            {usePin && (
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="4-6 digits"
                className="input w-24 text-sm py-1 text-center"
                maxLength={6}
              />
            )}
          </div>

          {/* Connection Types */}
          <div>
            <label className="block text-[10px] text-white/60 mb-1">Connections</label>
            <ConnectionTypeSelector
              value={allowedTransports}
              onChange={setAllowedTransports}
              compact
            />
          </div>

          {/* Error */}
          {error && (
            <div className="p-2 rounded bg-red-500/20 text-red-400 text-xs">
              {error}
            </div>
          )}

          {/* Submit - at bottom of scrollable content */}
          <button
            type="submit"
            disabled={isCreating || !location}
            className="w-full mt-4 py-3 rounded-xl bg-primary text-white font-bold text-base disabled:opacity-50"
            style={{ marginBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
          >
            {isCreating ? 'Creating...' : 'Create Room'}
          </button>
        </form>
      </main>
    </div>
  )
}
